// Geometric merge for the "effective zone" view. Overlapping same-class zones collapse into one block
// (no double-opacity mud, no internal seams), and where zones NEST, the SMALLER / more-specific zone
// wins the pixels — because in the real SHGM data both nest: a small green "serbest bölge" pocket sits
// inside a big red no-fly (an explicit allow carve-out), AND a small red "yasak bölge" sits inside a big
// green (a forbidden pocket in an allowed area). Verified against the live dataset (140 green-in-red,
// 124 red-in-green out of ~11k zones), so a blind "red always wins" rule would erase real exceptions.
//
// The merge runs ONCE on the full country dataset (heavy — moved to a Web Worker so it never freezes the
// UI). Each resulting footprint is TAGGED with its source zone's class/category/authority/type, so:
//   - filters (class, category, NOTAM type, authority) just show/hide footprints — NO recompute;
//   - hover flood-fills the CONTIGUOUS same-class group (adjacent yellows read + highlight as one).
// polygon-clipping (Martinez) does the union/difference; a coarse grid index keeps hole-punching ~O(N).

import polygonClipping from 'polygon-clipping';
import type { Zone, Notam } from './engine.ts';
import { zoneCategory } from './legend.ts';

type Ring = [number, number][];
type Poly = Ring[];
type MultiPoly = Poly[];
type Cls = 'nofly' | 'notam' | 'ok';
type Bbox = [number, number, number, number];

function toPoly(rings: number[][][]): Poly {
  return rings.map((r) => r.map((p) => [p[0], p[1]] as [number, number]));
}

function unionAll(polys: Poly[]): MultiPoly {
  if (polys.length === 0) return [];
  if (polys.length === 1) return polygonClipping.union(polys[0]);
  return polygonClipping.union(polys[0], ...polys.slice(1));
}

function polyArea(poly: Poly): number {
  const r = poly[0] ?? [];
  let a = 0;
  for (let i = 0; i < r.length - 1; i++) a += r[i][0] * r[i + 1][1] - r[i + 1][0] * r[i][1];
  return Math.abs(a / 2);
}
function polyBbox(poly: Poly): Bbox {
  let m0 = Infinity, n0 = Infinity, m1 = -Infinity, n1 = -Infinity;
  for (const ring of poly)
    for (const [x, y] of ring) {
      if (x < m0) m0 = x;
      if (y < n0) n0 = y;
      if (x > m1) m1 = x;
      if (y > n1) n1 = y;
    }
  return [m0, n0, m1, n1];
}
const bboxOverlap = (a: Bbox, b: Bbox): boolean => !(a[2] < b[0] || b[2] < a[0] || a[3] < b[1] || b[3] < a[1]);
// A small pad so footprints that ABUT (share a border, bboxes just touching) count as adjacent for the
// hover flood-fill. ~50 m in degrees — well under a zone's size, over floating-point boundary noise.
const ADJ_PAD = 0.0005;
const CELL = 0.1; // ~11 km grid cells for the spatial index
function cellsOf(bb: Bbox): string[] {
  const keys: string[] = [];
  for (let x = Math.floor(bb[0] / CELL); x <= Math.floor(bb[2] / CELL); x++)
    for (let y = Math.floor(bb[1] / CELL); y <= Math.floor(bb[3] / CELL); y++) keys.push(`${x}:${y}`);
  return keys;
}

/** One resolved footprint: a polygon (with nested stricter zones cut out as holes) + who it came from. */
export interface EffFootprint {
  cls: Cls;
  poly: Poly;
  bbox: Bbox;
  category: string; // zone category key (nofly/ok); '' for NOTAM
  authority: number; // zone authority code; -1 unknown / NOTAM
  ntype: string; // NOTAM area_type; '' for zones
}
export interface EffectiveGeom {
  footprints: EffFootprint[];
  /**
   * For each footprint, the indices of the footprints it TOUCHES. Built here, in the worker, and
   * carried with the geometry — see `buildAdjacency` for why it cannot be built on the hover path.
   */
  adjacency: number[][];
}

type Item = { cls: Cls; poly: Poly; area: number; bbox: Bbox; category: string; authority: number; ntype: string };

export function computeEffective(zones: Zone[], notams: Notam[], now: number): EffectiveGeom {
  const items: Item[] = [];
  const addZone = (z: Zone) => {
    const poly = toPoly(z.rings);
    if (!poly[0]?.length) return;
    const cls: Cls = z.area_type === 909 ? 'nofly' : 'ok';
    items.push({ cls, poly, area: polyArea(poly), bbox: polyBbox(poly), category: zoneCategory(z.name || '').key, authority: z.authority ?? -1, ntype: '' });
  };
  const addNotam = (n: Notam) => {
    const poly = toPoly(n.rings);
    if (!poly[0]?.length) return;
    items.push({ cls: 'notam', poly, area: polyArea(poly), bbox: polyBbox(poly), category: '', authority: -1, ntype: n.area_type || '?' });
  };
  for (const z of zones) addZone(z);
  for (const n of notams) if (n.start_ms <= now && now <= n.end_ms) addNotam(n);

  // Smallest first: each zone claims only the pixels no SMALLER zone already took, so the most-specific
  // rule wins wherever zones nest. Footprints stay per-source (tagged) — NOT unioned into one blob (a
  // country-wide union of ~10k polygons overflows polygon-clipping's sweep line, and per-source tags are
  // what let filters show/hide without recomputing). Abutting same-class fills read seamlessly (same
  // colour, no outline); the hover flood-fill re-groups them. Each hole-punch is wrapped so one bad
  // geometry degrades to the un-punched zone rather than crashing the whole merge.
  items.sort((a, b) => a.area - b.area);
  const grid = new Map<string, number[]>();
  const footprints: EffFootprint[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const seen = new Set<number>();
    for (const key of cellsOf(it.bbox)) for (const j of grid.get(key) ?? []) seen.add(j);
    const smaller = [...seen].map((j) => items[j]).filter((o) => bboxOverlap(o.bbox, it.bbox));
    let fp: MultiPoly = [it.poly];
    if (smaller.length) {
      try {
        const holes = unionAll(smaller.map((o) => o.poly));
        if (holes.length) fp = polygonClipping.difference([it.poly], holes);
      } catch {
        fp = [it.poly];
      }
    }
    for (const p of fp) footprints.push({ cls: it.cls, poly: p, bbox: polyBbox(p), category: it.category, authority: it.authority, ntype: it.ntype });
    for (const key of cellsOf(it.bbox)) {
      const arr = grid.get(key);
      if (arr) arr.push(i);
      else grid.set(key, [i]);
    }
  }
  return { footprints, adjacency: buildAdjacency(footprints) };
}

/** A footprint's filter tags — the renderer decides visibility, so the merge needn't be redone. */
export function footprintFeature(f: EffFootprint): GeoJSON.Feature {
  // Max bbox extent in degrees — lets the renderer give SMALL no-fly pockets a visible ring (they'd
  // otherwise sub-pixel away inside a big allowed area) while large zones stay fill-only (no seams).
  const maxext = Math.max(f.bbox[2] - f.bbox[0], f.bbox[3] - f.bbox[1]);
  return {
    type: 'Feature',
    properties: { cls: f.cls, category: f.category, authority: f.authority, ntype: f.ntype, maxext },
    geometry: { type: 'Polygon', coordinates: f.poly as unknown as GeoJSON.Position[][] },
  };
}
/** The per-class FeatureCollection for a source (all footprints of `cls`, tagged for setFilter). */
export function effClassFC(geom: EffectiveGeom, cls: Cls): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: geom.footprints.filter((f) => f.cls === cls).map(footprintFeature) };
}

// ---- point → merged component (for effective-mode hover highlight) ------------------------------
function pointInRing(ring: Ring, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
// A polygon (ring 0 = exterior, rest = holes): point is inside when an ODD number of rings contain it —
// so a point inside a hole (e.g. a red cut-out) counts as OUTSIDE.
function polyContains(poly: Poly, x: number, y: number): boolean {
  let c = 0;
  for (const ring of poly) if (pointInRing(ring, x, y)) c++;
  return c % 2 === 1;
}
const bboxHit = (b: Bbox, x: number, y: number): boolean => x >= b[0] && x <= b[2] && y >= b[1] && y <= b[3];

export type EffComponent = { cls: Cls; feature: GeoJSON.Feature };

/**
 * Which footprints TOUCH which.
 *
 * Adjacency used to be tested as "do the bounding boxes come within ADJ_PAD", which is wrong in a
 * way that grows with the size of the largest zone: a province-sized restriction's bbox overlaps
 * every same-class footprint anywhere inside that box, and the flood-fill is transitive, so it
 * chains straight through. Measured on the baked country dataset, hovering one polygon near Antalya
 * highlighted a "contiguous block" spanning 773 x 709 km — a third of Türkiye — as the area under
 * the cursor. It was also O(group x 11,500) per pointer move, which is where the 977 ms freeze came
 * from.
 *
 * Two footprints are adjacent here when their BOUNDARIES pass within about a cell of each other.
 * Each boundary vertex is dropped into an ADJ_PAD-sized cell and each cell is matched against its
 * 3x3 neighbourhood, so a pair straddling a cell edge still meets. That neighbourhood is not
 * optional: sampled over 410 starting footprints, restricting it to the exact cell changed 118 of
 * the groups, one of them by 388 members — real areas abut without sharing vertex coordinates.
 *
 * It is built HERE, with the merge, because doing it lazily on the main thread costs ~1.1 s the
 * first time a cursor crosses a zone. Here it is ~10% on top of a merge that already runs in a
 * worker, it is persisted with the geometry, and it adds 0.17 MB to a 14.6 MB structure. The
 * flood-fill it leaves behind is a graph walk: worst case 0.88 ms over 400 sampled starts.
 */
// Cells are ADJ_PAD on a side. |x/ADJ_PAD| <= 360_000 and |y/ADJ_PAD| <= 180_000, so packing y into
// the low 1e6 of a double is exact and collision-free for every coordinate on Earth.
const CELL_SPAN = 1_000_000;
/** Vertices above which a hover highlight is drawn un-merged rather than unioned. See its use. */
const UNION_VERTEX_CAP = 12_000;
const cellKey = (x: number, y: number): number => Math.round(x / ADJ_PAD) * CELL_SPAN + Math.round(y / ADJ_PAD);

function buildAdjacency(footprints: EffFootprint[]): number[][] {
  const cellsOf: Array<Set<number>> = [];
  const byCell = new Map<number, number[]>();
  for (const f of footprints) {
    const set = new Set<number>();
    for (const ring of f.poly) for (const [x, y] of ring) set.add(cellKey(x, y));
    cellsOf.push(set);
  }
  cellsOf.forEach((set, i) => {
    for (const key of set) {
      const at = byCell.get(key);
      if (at) at.push(i);
      else byCell.set(key, [i]);
    }
  });
  const neighbours = footprints.map(() => new Set<number>());
  for (let i = 0; i < footprints.length; i++) {
    for (const cell of cellsOf[i]) {
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (const j of byCell.get(cell + dx * CELL_SPAN + dy) ?? []) {
            if (j === i) continue;
            neighbours[i].add(j);
            neighbours[j].add(i);
          }
        }
      }
    }
  }
  return neighbours.map((set) => [...set]);
}

/**
 * The last group's union, reused while the cursor stays inside it.
 *
 * Hover fires on every pointer move, and the answer is the same for every pixel of one block — so
 * without this the same several-hundred-polygon union is recomputed dozens of times per second.
 */
let lastGroup: { key: string; component: EffComponent } | null = null;

/**
 * The CONTIGUOUS same-class block under a point: flood-fill from the footprint under the cursor across
 * TOUCHING same-class footprints that pass `visible` (so filters stay consistent), then union the group
 * into one geometry — adjacent yellows highlight together, not one-at-a-time. `visible` defaults to all.
 */
export function effComponentAt(
  geom: EffectiveGeom,
  lon: number,
  lat: number,
  visible: (f: EffFootprint) => boolean = () => true,
): EffComponent | null {
  const fps = geom.footprints;
  const adj = geom.adjacency;
  let startIdx = -1;
  for (let i = 0; i < fps.length; i++) {
    const f = fps[i];
    if (visible(f) && bboxHit(f.bbox, lon, lat) && polyContains(f.poly, lon, lat)) {
      startIdx = i;
      break;
    }
  }
  if (startIdx < 0) return null;
  const cls = fps[startIdx].cls;
  // BFS over TOUCHING same-class visible footprints, along the prebuilt adjacency list.
  const inGroup = new Set<number>([startIdx]);
  const queue = [startIdx];
  while (queue.length) {
    const cur = queue.pop()!;
    for (const j of adj[cur] ?? []) {
      if (inGroup.has(j) || fps[j].cls !== cls || !visible(fps[j])) continue;
      inGroup.add(j);
      queue.push(j);
    }
  }
  const members = [...inGroup].sort((a, b) => a - b);
  const key = `${cls}:${members.join(',')}`;
  if (lastGroup && lastGroup.key === key) return lastGroup.component;

  const polys = members.map((i) => fps[i].poly);
  let merged: MultiPoly = polys.map((p) => p);
  // Unioning the group removes internal seams from the highlight. Its cost tracks VERTICES, not
  // members: the largest group here is 392 footprints and unions in ~50 ms, while a 327-footprint
  // one carrying 100k vertices takes 926 ms — inside a pointer-move handler. Above the cap the
  // group is highlighted un-merged, which is already the accepted rendering when a union throws,
  // and at the zoom where a group this sprawling fits on screen its internal seams are sub-pixel.
  //
  // 12,000 chosen by measurement over every multi-member group in the country dataset: 1,025 of
  // 1,028 still union exactly as before, and the worst union falls from 926 ms to 20 ms.
  const vertices = polys.reduce((n, poly) => n + poly.reduce((m, ring) => m + ring.length, 0), 0);
  if (polys.length > 1 && vertices <= UNION_VERTEX_CAP) {
    try {
      merged = unionAll(polys);
    } catch {
      merged = polys.map((p) => p); // union failed → highlight the group un-merged (minor internal seams)
    }
  }
  const component: EffComponent = {
    cls,
    feature: { type: 'Feature', properties: {}, geometry: { type: 'MultiPolygon', coordinates: merged as unknown as GeoJSON.Position[][][] } },
  };
  lastGroup = { key, component };
  return component;
}
